import React, { useMemo } from 'react';
import { useForm, useFieldArray } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { Button } from 'primereact/button';
import { ProgressSpinner } from 'primereact/progressspinner';
import FormDropdown from '../../components/form/FormDropdown';
import FormInputText from '../../components/form/FormInputText';
import FormPassword from '../../components/form/FormPassword';
import { IntegrationSettings } from '../../models/integrationSettings';
import { IntegrationStatus, IntegrationVendor } from '../../models/enums/integrationEnums';
import { parseIntegrationData } from '../../utils/misc-utils';
import { useEnumValues } from '../../hooks/useEnumValues';
import { EnumValueData } from '../../models/enum';
import { identifierField, secretField, shortTextField } from '../../utils/validation-helper';

type SubscriberCodeMapping = {
  permissiblePurpose: string;
  subscriberCode: string;
};

type ExperianVoieFormValues = {
  accountIdentifier: string;
  accountPassword: string;
  clientId: string;
  clientSecret: string;
  subscriberCodes: SubscriberCodeMapping[];
};

interface EditExperianVoieIntegrationSettingsProps {
  onCancel: () => void;
  onSave?: (settings: IntegrationSettings) => void;
  integrationSettings?: IntegrationSettings;
  environment: string;
}

interface ExperianVoieFormProps {
  onCancel: () => void;
  onSave?: (settings: IntegrationSettings) => void;
  integrationSettings?: IntegrationSettings;
  environment: string;
  permissiblePurposes: EnumValueData[];
  isErrorPurposes: boolean;
}

/**
 * Parent component: fetches enum data and shows a spinner until ready.
 * The form only mounts after permissiblePurposes are loaded, so useForm
 * initializes with the correct default values from the start.
 */
export const EditExperianVoieIntegrationSettings = ({
  onCancel,
  onSave,
  integrationSettings,
  environment,
}: EditExperianVoieIntegrationSettingsProps): JSX.Element => {
  const { data: enumValues, isLoading: isLoadingPurposes, isError: isErrorPurposes } = useEnumValues('Core.ThirdParty.IncomeEmploymentAsset.Vendors.Experian.Models.PermissiblePurposeCode');
  const permissiblePurposes = useMemo(
    () => (enumValues ?? []).filter((v) => !v.isHidden).sort((a, b) => a.order - b.order),
    [enumValues]
  );

  if (isLoadingPurposes) {
    return (
      <div className="surface-section p-4 border-1 surface-border border-round mb-3 flex justify-content-center">
        <ProgressSpinner style={{ width: '50px', height: '50px' }} />
      </div>
    );
  }

  return (
    <ExperianVoieForm
      onCancel={onCancel}
      onSave={onSave}
      integrationSettings={integrationSettings}
      environment={environment}
      permissiblePurposes={permissiblePurposes}
      isErrorPurposes={isErrorPurposes}
    />
  );
};

/**
 * Child component: only mounts after permissiblePurposes are loaded.
 * useForm initializes with complete default values — no reset needed.
 * Subscriber codes are dynamic rows: each row has a purpose dropdown + code input + delete.
 */
const ExperianVoieForm = ({
  onCancel,
  onSave,
  integrationSettings,
  environment,
  permissiblePurposes,
  isErrorPurposes,
}: ExperianVoieFormProps): JSX.Element => {
  const isEditMode = !!integrationSettings;

  let accountIdentifier = '';
  let accountPassword = '';
  let clientId = '';
  let clientSecret = '';
  let defaultSubscriberCodes: SubscriberCodeMapping[] = [];
  if (isEditMode && integrationSettings?.integrationData) {
    const data = parseIntegrationData(integrationSettings.integrationData);
    accountIdentifier = data.accountIdentifier || '';
    accountPassword = data.accountPassword || '';
    clientId = data.clientId || '';
    clientSecret = data.clientSecret || '';
    const savedCodes = data.subscriberCodes as SubscriberCodeMapping[] | undefined;
    if (savedCodes && Array.isArray(savedCodes)) {
      defaultSubscriberCodes = savedCodes.map((sc) => ({
        permissiblePurpose: sc.permissiblePurpose || '',
        subscriberCode: sc.subscriberCode || '',
      }));
    }
  }

  const validationSchema = yup.object().shape({
    accountIdentifier: identifierField('Account Identifier').required('Account Identifier is required'),
    accountPassword: secretField('Account Password').required('Account Password is required'),
    clientId: identifierField('Client ID').required('Client ID is required'),
    clientSecret: secretField('Client Secret').required('Client Secret is required'),
    subscriberCodes: yup.array().of(
      yup.object().shape({
        permissiblePurpose: shortTextField('Permissible Purpose').required('Permissible Purpose is required'),
        subscriberCode: identifierField('Subscriber Code').required('Subscriber Code is required'),
      })
    ).required(),
  });

  const { control, handleSubmit, formState, watch } = useForm<ExperianVoieFormValues>({
    defaultValues: {
      accountIdentifier,
      accountPassword,
      clientId,
      clientSecret,
      subscriberCodes: defaultSubscriberCodes,
    },
    resolver: yupResolver(validationSchema),
    mode: 'onSubmit',
  });

  const { errors } = formState;

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'subscriberCodes',
  });

  const watchedSubscriberCodes = watch('subscriberCodes');

  // Build dropdown options, filtering out already-selected purposes
  const getAvailablePurposes = (currentIndex: number) => {
    const selectedPurposes = (watchedSubscriberCodes || [])
      .filter((_: SubscriberCodeMapping, i: number) => i !== currentIndex)
      .map((sc: SubscriberCodeMapping) => sc.permissiblePurpose);
    return permissiblePurposes
      .filter((p) => !selectedPurposes.includes(p.name || ''))
      .map((p) => ({
        label: p.displayName || p.name || '',
        value: p.name || '',
      }));
  };

  const onSubmit = (data: ExperianVoieFormValues) => {
    if (onSave) {
      const subscriberCodes = (data.subscriberCodes || []).map((sc) => ({
        permissiblePurpose: sc.permissiblePurpose,
        subscriberCode: sc.subscriberCode,
      }));

      let settings: IntegrationSettings;

      if (isEditMode && integrationSettings) {
        settings = {
          ...integrationSettings,
          environment: environment,
          integrationData: JSON.stringify({
            accountIdentifier: data.accountIdentifier,
            accountPassword: data.accountPassword,
            clientId: data.clientId,
            clientSecret: data.clientSecret,
            subscriberCodes,
          }),
        };
      } else {
        settings = {
          name: 'Experian VOIE Integration',
          vendor: IntegrationVendor.Experian,
          status: IntegrationStatus.Active,
          environment: environment,
          integrationData: JSON.stringify({
            accountIdentifier: data.accountIdentifier,
            accountPassword: data.accountPassword,
            clientId: data.clientId,
            clientSecret: data.clientSecret,
            subscriberCodes,
          }),
        };
      }

      onSave(settings);
    }
  };

  return (
    <div className="surface-section p-4 border-1 surface-border border-round mb-3">
      <h3 className="mt-0 mb-3">{isEditMode ? 'Edit Experian VOIE Settings' : 'Experian VOIE Settings'}</h3>
      <form onSubmit={handleSubmit(onSubmit)} noValidate className="pb-3 p-fluid p-input-filled">
        <div className="field mb-4">
          <FormInputText
            control={control}
            errors={errors}
            field="accountIdentifier"
            label="Account Identifier *"
            placeholder="Enter your Experian Account Identifier"
          />
        </div>

        <div className="field mb-4">
          <FormPassword
            control={control}
            errors={errors}
            field="accountPassword"
            label="Account Password *"
            placeholder="Enter your Experian Account Password"
            feedback={false}
            toggleMask
          />
        </div>

        <div className="field mb-4">
          <FormInputText
            control={control}
            errors={errors}
            field="clientId"
            label="Client ID *"
            placeholder="Enter your Experian Client ID"
          />
        </div>

        <div className="field mb-4">
          <FormPassword
            control={control}
            errors={errors}
            field="clientSecret"
            label="Client Secret *"
            placeholder="Enter your Experian Client Secret"
            feedback={false}
            toggleMask
          />
        </div>

        <h4 className="mt-2 mb-3">Subscriber Codes by Permissible Purpose</h4>
        {isErrorPurposes && (
          <div className="p-3 mb-3 border-round" style={{ backgroundColor: 'var(--red-50)', color: 'var(--red-700)' }}>
            <i className="pi pi-exclamation-triangle mr-2" />
            Unable to load permissible purposes. Subscriber code fields cannot be displayed.
          </div>
        )}
        {!isErrorPurposes && fields.map((field, index) => (
          <div key={field.id} className="flex align-items-start gap-2 mb-3">
            <div className="flex-1">
              <FormDropdown
                control={control}
                errors={errors}
                field={`subscriberCodes.${index}.permissiblePurpose`}
                label="Permissible Purpose"
                options={getAvailablePurposes(index)}
                placeholder="Select Permissible Purpose"
                required
              />
            </div>
            <div className="flex-1">
              <FormInputText
                control={control}
                errors={errors}
                field={`subscriberCodes.${index}.subscriberCode`}
                label="Subscriber Code *"
                placeholder="Enter subscriber code"
              />
            </div>
            <Button
              type="button"
              icon="pi pi-trash"
              className="p-button-danger p-button-text mt-4"
              onClick={() => remove(index)}
              style={{ minWidth: 'auto', width: 'auto' }}
            />
          </div>
        ))}
        {!isErrorPurposes && permissiblePurposes.length > 0 && fields.length < permissiblePurposes.length && (
          <Button
            type="button"
            label="Add Purpose"
            icon="pi pi-plus"
            className="p-button-outlined mb-4"
            onClick={() => append({ permissiblePurpose: '', subscriberCode: '' })}
            style={{ width: 'auto', minWidth: 'auto' }}
          />
        )}

        <div className="flex justify-content-end gap-2 pt-4">
          <Button
            type="button"
            label="Cancel"
            className="p-button-outlined"
            style={{ width: 'auto', minWidth: 'auto' }}
            onClick={onCancel}
          />
          <Button
            type="submit"
            label={isEditMode ? 'Update' : 'Save'}
            className="p-button"
            style={{ width: 'auto', minWidth: 'auto' }}
          />
        </div>
      </form>
    </div>
  );
};
